import Button from "@material-ui/core/Button/Button";
import CircularProgress from "@material-ui/core/CircularProgress/CircularProgress";
import FormControl from "@material-ui/core/FormControl/FormControl";
import FormLabel from "@material-ui/core/FormLabel/FormLabel";
import TextField, { TextFieldProps } from "@material-ui/core/TextField/TextField";
import Typography from "@material-ui/core/Typography/Typography";
import Save from "@material-ui/icons/Save";
import * as React from "react";
import { DocumentModel } from "../../model/autogen/DocumentModel";
import { DocumentResult } from "../../model/autogen/DocumentResult";
import { ResolveTagsInputModel } from "../../model/autogen/ResolveTagsInputModel";
import { ResolvePeopleInputModel } from "../../model/autogen/ResolvePeopleInputModel";
import { TagRequestFactory } from "../../model/autogen/TagController";
import { TagModel } from "../../model/autogen/TagModel";
import { WithDefaultStyles, withDefaultStyles } from "../../utils/createStyles";
import { reOrderDocumentItems } from "../../utils/document-order";
import { NBSP } from "../../utils/string";
import { EditableDocumentItemList, NoEditableDocuments } from "../editable-document-item-list";
import TagListEditor from "../material/tag-list-editor";
import { IAuthContextProps, IFetcher, withAuthContext } from "../with-auth-context";
import { ISnackbarContextProps, withSnackbar } from "../with-snackbar";
import { IContentForm } from "./icontent-form";
import { RTEField } from "./rte-field";
import { isArrayNullOrEmpty } from "../../utils/array";
import { UserLevel } from "../../model/autogen/UserLevel";
import { PersonModel } from "../../model/autogen/PersonModel";
import { PeopleRequestFactory } from "../../model/autogen/PeopleController";
import { QuoteModel } from "../../model/autogen/QuoteModel";
import { QuoteRequestFactory } from "../../model/autogen/QuoteController";
import { AbstractDocumentKind } from "../../model/autogen/AbstractDocumentKind";

const shortid = require("shortid");

export interface ICreateTags {
    createTags: TagModel[];
}

export interface IDocumentEditModelState extends DocumentModel {
    isSaving: boolean;
}

export type DocumentCreator<TCreateModel> = (fetcher: IFetcher, model: TCreateModel) => Promise<DocumentResult>;

function prepareCreatePayload<TState extends IDocumentEditModelState & ICreateTags>(state: TState): Exclude<TState, IDocumentEditModelState> {
    //We want below, but TS won't let us (https://github.com/Microsoft/TypeScript/issues/16780)
    //const { isSaving, ...payload } = state;
    //return payload;

    //So we have to resort to this hackery!
    let payload = { ...(state as any) };
    delete payload.isSaving;

    return payload;
}

export function resolveTagsHelper(callApi: (serviceMethod: (fetcher: IFetcher) => Promise<TagModel[]>) => Promise<TagModel[]>, tags: string[]): Promise<TagModel[]> {
    if (!isArrayNullOrEmpty(tags)) {
        const m = {
            tags: [...tags]
        } as ResolveTagsInputModel;
        return callApi((fetcher) => TagRequestFactory.resolveTags(fetcher, m));
    }
    return Promise.resolve([]);
}

export function resolvePeopleHelper(callApi: (serviceMethod: (fetcher: IFetcher) => Promise<PersonModel[]>) => Promise<PersonModel[]>, peopleIds: number[]): Promise<PersonModel[]> {
    if (!isArrayNullOrEmpty(peopleIds)) {
        const p = {
            peopleIds: [...peopleIds]
        } as ResolvePeopleInputModel;
        return callApi((fetcher) => PeopleRequestFactory.resolvePeople(fetcher, p));
    }
    return Promise.resolve([]);
}

export function findTagsHelper(callApi: (serviceMethod: (fetcher: IFetcher) => Promise<TagModel[]>) => Promise<TagModel[]>, query: string, isResourceOnly: boolean): Promise<TagModel[]> {
    return callApi((fetcher) => isResourceOnly
        ? TagRequestFactory.findResourceOnly(fetcher, query)
        : TagRequestFactory.find(fetcher, query));
}

export function findPeopleHelper(callApi: (serviceMethod: (fetcher: IFetcher) => Promise<PersonModel[]>) => Promise<PersonModel[]>, query: string): Promise<PersonModel[]> {
    return callApi((fetcher) => PeopleRequestFactory.find(fetcher, query));
}

export function findQuotesHelper(callApi: (serviceMethod: (fetcher: IFetcher) => Promise<QuoteModel[]>) => Promise<QuoteModel[]>, personId: number): Promise<QuoteModel[]> {
    return callApi((fetcher) => QuoteRequestFactory.getQuotes(fetcher, personId));
}

interface IDocumentTextFieldProps<TModel> {
    label: string;
    name: string;
    value: string;
    extraProps?: TextFieldProps;
}

interface IDocumentListTagEditorProps {
    label?: string
}

interface ControllerStateAndHelpers<TModel> {
    DocumentItemListEditor: () => JSX.Element;
    DocumentTagListEditor: (props: IDocumentListTagEditorProps) => JSX.Element;
    DocumentFormSubmitButton: () => JSX.Element;
    DocumentTextField: (props: IDocumentTextFieldProps<TModel>) => JSX.Element;
    DocumentRTEField: (props: IDocumentTextFieldProps<TModel>) => JSX.Element;
}

export type ChildrenFunction<TModel> = (
    options: ControllerStateAndHelpers<TModel>,
) => React.ReactNode

export type IFormStateCoreProps<TModel> = IContentForm<TModel> & {
    documentItemTypeName: string,
    documentItemTypeNamePlural: string,
    submitAction: DocumentCreator<ICreateTags>,
    documentTypeName: string,
    children: ChildrenFunction<TModel>,
    initialTags: string[],
    cannotSave: boolean,
    onPrepareForSave?: (localState: IDocumentEditModelState & ICreateTags) => TModel;
    onChange: (key: keyof TModel, val: any) => void
};

export type IFormStateProps<TModel> = IFormStateCoreProps<TModel> & IAuthContextProps & ISnackbarContextProps & WithDefaultStyles;

class FormStateHelperComponentInner<TModel extends IDocumentEditModelState & ICreateTags> extends React.Component<IFormStateProps<TModel>, IDocumentEditModelState & ICreateTags> {
    constructor(props: IFormStateProps<TModel>) {
        super(props);
        // stupid - https://github.com/Microsoft/TypeScript/pull/13288
        const item: any = props.item;

        this.state = { isSaving: false, isSnackbarOpen: false, snackbarMessage: "", ...item }
    }

    private onTextChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        this.props.onChange(e.target.name as keyof TModel, e.target.value);
        this.setState({ [e.target.name]: e.target.value } as any);
    }
    private onItemChange = (index: number, title: string, value: string) => {
        const { items } = this.state;
        if (items[index]) {
            items[index].title = title;
            items[index].content = value;
            this.setState({ items });
        }
    }
    private onAddItem = () => {
        const { items } = this.state;
        const { documentItemTypeName } = this.props;
        items.push({
            guid: shortid.generate(),
            inline: false,
            title: `New ${documentItemTypeName}`,
            content: `Enter details of your ${documentItemTypeName} here`,
            order: items.length
        });
        this.setState({ items });
    }
    private onRemoveItem = (index: number) => {
        let { items } = this.state;
        items.splice(index, 1);
        this.setState({ items });
    }
    private onSave = async (e: any) => {
        const state = this.state;
        const payload: any = prepareCreatePayload(state);
        let savePayload = payload;
        if (this.props.onPrepareForSave) {
            savePayload = this.props.onPrepareForSave(payload);
        }
        const { callApi, showSnackbarMessage } = this.props;
        this.setState({ isSaving: true });
        try {
            const result = await callApi((fetcher) => this.props.submitAction(fetcher, savePayload));
            if(this.props.user.level <= UserLevel.External)
            {
                showSnackbarMessage("Your content has been successfully submitted. It will be reviewed and posted by a Research Hub editor. Any questions, please email researchhub@labour.org.nz");
                
            }
            else
            {
                showSnackbarMessage(`${(this.props.isEditing === true) ? "" : "New "}${this.props.documentTypeName} saved (${result.documentId})`);
            }
            this.props.onSaveCompleted();
        } catch (e) {
            this.setState({ isSaving: false });
            showSnackbarMessage(`Error saving ${this.props.documentTypeName}`);
        }
    }
    private onTagsChanged = (tags: TagModel[]) => {
        this.props.onChange("createTags", tags);
        this.setState({ createTags: tags });
    }
    private onSearchTags = (query: string, isResourceOnly: boolean) => {
        return findTagsHelper(this.props.callApi, query, isResourceOnly);
    }
    private onResolveTags = (tags: string[]) => {
        return resolveTagsHelper(this.props.callApi, tags);
    }
    private onDocumentItemsReordered = (newItemOrder: { id: string }[]) => {
        const { items } = this.state;
        const newOrderedItems = reOrderDocumentItems(items, newItemOrder);
        this.setState({ items: newOrderedItems });
    }
    public render() {
        return this.props.children({
            DocumentItemListEditor: this.DocumentItemListEditor,
            DocumentTagListEditor: this.DocumentTagListEditor,
            DocumentFormSubmitButton: this.DocumentFormSubmitButton,
            DocumentTextField: this.DocumentTextField,
            DocumentRTEField: this.DocumentRTEField
        });
    }
    public DocumentRTEField = (props: IDocumentTextFieldProps<TModel>): JSX.Element => {
        return <FormControl className={this.props.classes.input} >
            <FormLabel>{props.label}</FormLabel>
            <RTEField name={props.name} onChange={this.onTextChange} value={props.value} />
        </FormControl>;
    }
    public DocumentTextField = (props: IDocumentTextFieldProps<TModel>): JSX.Element => {
        return <TextField className={this.props.classes.input} label={props.label} fullWidth={true} name={`${props.name}`} value={props.value} onChange={this.onTextChange} disabled={this.state.isSaving} {...(props.extraProps || {})} />
    }
    public DocumentItemListEditor = () => {
        const state = this.state;
        return <div className={this.props.classes.input} >
            <Typography variant="h6">{this.props.documentItemTypeNamePlural}{state.items.length > 0 ? ` (${state.items.length} item(s))` : undefined}</Typography>
            <EditableDocumentItemList onReordered={this.onDocumentItemsReordered} items={state.items} onChange={this.onItemChange} onAdd={this.onAddItem} onRemove={this.onRemoveItem} noItemsPlaceholder={<NoEditableDocuments itemKindPlural={this.props.documentItemTypeNamePlural}
                newItemKindSingular={`a new ${this.props.documentItemTypeName}`} />} />
        </div>;
    }
    public DocumentTagListEditor = (props: IDocumentListTagEditorProps) => {
        return <FormControl className={this.props.classes.input} >
            <FormLabel>{props.label || "Tags"}</FormLabel>
            <TagListEditor isResourceOnly={this.props.documentTypeName === AbstractDocumentKind.Resource} initialTags={this.props.initialTags} onTagsChanged={this.onTagsChanged} onFetchTags={this.onSearchTags} onResolveTags={this.onResolveTags} />
        </FormControl>;
    }
    public DocumentFormSubmitButton = () => {
        const { cannotSave } = this.props;
        const { isSaving } = this.state;
        return <Button className={this.props.classes.input} variant="contained" fullWidth={true} onClick={this.onSave} disabled={isSaving || cannotSave} color="primary">
            {isSaving ? <CircularProgress size={24} /> : <Save />}{NBSP}{isSaving ? "Saving ..." : "Save"}
        </Button>;
    }
}

export const FormStateHelperComponent = withSnackbar(withAuthContext(withDefaultStyles(FormStateHelperComponentInner))) as React.ComponentType<IFormStateCoreProps<any>>;


// WEBPACK FOOTER //
// ./src/components/forms/form-state-helper.tsx